import { type FragmentOf, type ResultOf, readFragment } from "@opensea/graphql"
import { PreviewImageFragment } from "@/components/collection/drops/components/PreviewImages/PreviewImage.fragment"
import { PreviewImagesFragment } from "@/components/collection/drops/components/PreviewImages/PreviewImages.fragment"
import { CollectionOverviewModulesFragment } from "../fragments"
import { HeroMediaFragment } from "../fragments/HeroMediaFragment"
import type {
  CollectionOverviewMediaType,
  HeroMediaData,
  PreviewImagesData,
} from "../types"
import { getMediaPropsFromMedia } from "../utils/media"

type ParsedModuleData = NonNullable<
  NonNullable<
    ResultOf<typeof CollectionOverviewModulesFragment>["overview"]
  >["modules"]
>[number]

/**
 * Parses collection modules from GraphQL fragment data
 * Filters out null modules and returns typed module data
 */
export function parseCollectionModules(
  collectionKey: FragmentOf<typeof CollectionOverviewModulesFragment>,
): ParsedModuleData[] | null {
  const collectionData = readFragment(
    CollectionOverviewModulesFragment,
    collectionKey,
  )
  const rawModules = collectionData.overview?.modules ?? null

  if (!rawModules) {
    return null
  }

  return rawModules
    .map(module => {
      if (!module) {
        return null
      }

      switch (module.__typename) {
        case "CollectionContentBlockModule":
        case "CollectionTeamModule":
        case "CollectionNarrativeModule":
        case "CollectionFAQModule":
          return module
        default:
          return null
      }
    })
    .filter((module): module is ParsedModuleData => module !== null)
}

/**
 * Gets raw modules from collection fragment data without parsing
 * Used when you need the original module structure
 */
export function getRawCollectionModules(
  collectionKey: FragmentOf<typeof CollectionOverviewModulesFragment>,
):
  | NonNullable<
      ResultOf<typeof CollectionOverviewModulesFragment>["overview"]
    >["modules"]
  | null {
  const collectionData = readFragment(
    CollectionOverviewModulesFragment,
    collectionKey,
  )
  return collectionData.overview?.modules ?? null
}

/**
 * Parses raw modules array directly without requiring collection fragment
 * Used for syncing modules from external sources
 */
export function parseRawModules(
  rawModules:
    | NonNullable<
        ResultOf<typeof CollectionOverviewModulesFragment>["overview"]
      >["modules"]
    | null,
): ParsedModuleData[] | null {
  if (!rawModules) {
    return null
  }

  return rawModules
    .map(module => {
      if (!module) {
        return null
      }

      switch (module.__typename) {
        case "CollectionContentBlockModule":
        case "CollectionTeamModule":
        case "CollectionNarrativeModule":
        case "CollectionFAQModule":
          return module
        default:
          return null
      }
    })
    .filter((module): module is ParsedModuleData => module !== null)
}

/**
 * Parses preview images from collection fragment data
 * Transforms GraphQL preview media to store format
 */
export function parseCollectionPreviewImages(
  collectionKey: FragmentOf<typeof PreviewImagesFragment>,
): PreviewImagesData {
  const collectionData = readFragment(PreviewImagesFragment, collectionKey)
  const previewMedia = collectionData.about?.previewMedia ?? []

  return previewMedia
    .map(media => {
      // First read the PreviewImage fragment to get the MediaProps fragment
      const previewImageData = readFragment(PreviewImageFragment, media)
      const mediaProps = getMediaPropsFromMedia(previewImageData)
      if (!mediaProps) {
        return null
      }
      return {
        __typename: mediaProps.mediaType,
        imageUrl: mediaProps.imageUrl || undefined,
        videoUrl: mediaProps.videoUrl || undefined,
        muxPlaybackId: mediaProps.muxPlaybackId || undefined,
        aspectRatio: mediaProps.aspectRatio || undefined,
        thumbnailUrl: mediaProps.thumbnailUrl || undefined,
      }
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
}

/**
 * Parses hero media from collection fragment data
 * Transforms GraphQL hero media to store format
 */
export function parseCollectionHeroMedia(
  collectionKey: FragmentOf<typeof HeroMediaFragment>,
): HeroMediaData {
  const collectionData = readFragment(HeroMediaFragment, collectionKey)

  let desktopHeroMedia: CollectionOverviewMediaType | null = null
  let mobileHeroMedia: CollectionOverviewMediaType | null = null

  if (collectionData.hero?.desktopHeroMedia) {
    const mediaProps = getMediaPropsFromMedia(
      collectionData.hero.desktopHeroMedia,
    )
    if (mediaProps) {
      desktopHeroMedia = mediaProps
    }
  }

  if (collectionData.hero?.mobileHeroMedia) {
    const mediaProps = getMediaPropsFromMedia(
      collectionData.hero.mobileHeroMedia,
    )
    if (mediaProps) {
      mobileHeroMedia = mediaProps
    }
  }

  return {
    desktopHeroMedia,
    mobileHeroMedia,
  }
}
